import { useState, useEffect, useRef, useCallback } from "react";
import { resolveAssetUrl } from "../engine/assetRoot";

interface VideoPlayerProps {
  asset: string;
  autoplay: boolean;
  skipAfter?: number | undefined;
  onEnd: () => void;
}

export function VideoPlayer({ asset, autoplay, skipAfter, onEnd }: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [canSkip, setCanSkip] = useState(skipAfter === undefined || skipAfter <= 0);
  const skipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (skipAfter !== undefined && skipAfter > 0) {
      skipTimerRef.current = setTimeout(() => {
        setCanSkip(true);
        skipTimerRef.current = null;
      }, skipAfter * 1000);
    }
    return () => {
      if (skipTimerRef.current !== null) {
        clearTimeout(skipTimerRef.current);
      }
    };
  }, [skipAfter]);

  const handleEnded = useCallback(() => {
    onEnd();
  }, [onEnd]);

  const handleSkip = useCallback(() => {
    if (canSkip) {
      if (videoRef.current) {
        videoRef.current.pause();
      }
      onEnd();
    }
  }, [canSkip, onEnd]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.key === " " || e.key === "Enter" || e.key === "Escape") && canSkip) {
        e.preventDefault();
        handleSkip();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [canSkip, handleSkip]);

  return (
    <div
      onClick={handleSkip}
      style={{
        position: "absolute",
        inset: 0,
        background: "#000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 200,
        cursor: canSkip ? "pointer" : "default",
      }}
    >
      <video
        ref={videoRef}
        src={resolveAssetUrl(asset)}
        autoPlay={autoplay}
        onEnded={handleEnded}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
        }}
      />
      {canSkip && (
        <span
          style={{
            position: "absolute",
            bottom: "2rem",
            right: "2rem",
            padding: "0.4rem 1rem",
            background: "rgba(255, 255, 255, 0.15)",
            border: "1px solid rgba(255, 255, 255, 0.3)",
            borderRadius: "6px",
            fontSize: "0.85rem",
            color: "#eee",
            opacity: 0.8,
          }}
        >
          Click or press Space to skip
        </span>
      )}
    </div>
  );
}
